import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import Page from './components/elements/Page'
import { ScrollToTop } from './components/hocs/ScrollToTop'
import Pages from './components/pages/Pages'
import { clearStoredRedirectPath, getPostLoginRedirectPath } from './constants/redirectPaths'
import './styles/style.scss'

import * as Sentry from '@sentry/react'
import { useGetCurrentUserProfile } from './hooks/apolloHooks/useGetCurrentUserProfile'
import useSetupMatomo from './hooks/useSetupMatomo'

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

interface AppProps {
  /** Redirect path passed from AuthenticatedApp, captured at moment of authentication */
  redirectPath?: string | null;
}

export const App: React.FC<AppProps> = ({ redirectPath }) => {
  useSetupMatomo()
  const { userProfile } = useGetCurrentUserProfile()
  const isSuperUser = React.useMemo(
    () => userProfile?.roles?.some(role => role.claimName === 'Superuser') ?? false,
    [userProfile]
  )

  // Use the redirect path passed from AuthenticatedApp, or fall back to calculating it
  // The path is captured in AuthenticatedApp at the moment of authentication,
  // before any React re-renders that might clear sessionStorage
  const redirectPathRef = React.useRef<string | null>(null);
  if (redirectPathRef.current === null) {
    // If a path was passed from AuthenticatedApp, use it
    // Otherwise, try to get it from sessionStorage (might be null if already cleared)
    redirectPathRef.current = redirectPath || getPostLoginRedirectPath();
  }

  // Clear the stored path after component mounts (navigation happens)
  // This ensures we don't keep redirecting on subsequent page loads
  React.useEffect(() => {
    // Clear after a short delay to ensure navigation has completed
    const timer = setTimeout(() => {
      clearStoredRedirectPath();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Token refresh is now handled automatically by Apollo link
  // Every GraphQL request will check and refresh the token if needed

  return (
    <>
      <ScrollToTop />
      <Page>
        <SentryRoutes>
          {/* Root path redirect - check for stored path or use default dashboard */}
          <Route
            element={<Navigate to={redirectPathRef.current} replace />}
            path="/"
          />
          {/* Mitt Konto routes */}
          <Route element={<Pages.DashboardPage />} path="/mitt-konto/dashboard" />
          <Route element={<Navigate to="/mitt-konto/dashboard" replace />} path="/mitt-konto" />
          <Route element={<Pages.InvoicesStatementsPage />} path="/mitt-konto/fakturor-kontobesked" />
          <Route element={<Pages.ProfileSettingsPage />} path="/mitt-konto/installningar" />
          <Route element={<Pages.UserManagementPage />} path="/mitt-konto/hantera-anvandare" />

          {/* Other routes */}
          <Route element={<Pages.TransactionsPage />} path="/transaktioner/" />
          <Route element={<Pages.LogoutPage />} path="/loggaut" />
          <Route element={<Pages.CardsPage />} path="/kort" />
          <Route element={<Pages.ContactPage />} path="/kontakt" />

          {/* Only Superuser can access this route */}
          {isSuperUser && (
            <>
              <Route
                element={<Pages.DeleteAccountPage />}
                path="/avsluta-konto"
              />
            </>
          )}
          <Route element={<Pages.NotFoundPage />} path="*" />
        </SentryRoutes>
      </Page>
    </>
  )
}

export default App
